import { c as _c } from "react/compiler-runtime";
import { centsToUnits, CurrencyEnum } from '@systemeio/ui';
import { CloseIcon, InfoIcon } from '@systemeio/ui/icons';
import { useSessionStore } from '@systemeio/util';
import { useTotalRevenueLimit } from 'modules/main/hooks/use-total-revenue-limit';
import { useMemo, useState } from 'react';
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch';
import TransLoco from 'shared/components/trans-loco';
import { pageRoute } from 'shared/constants/routes.constants';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import useUser from 'shared/hooks/use-user';
import { currencyFormat } from 'shared/utils/currency-format';
import { dateFormat } from 'shared/utils/date-time-format';
import { getPageRoute } from 'shared/utils/get-dynamic-page-route';
import { twJoin } from 'tailwind-merge';
const MIN_REVENUE_LIMIT_PERCENTAGE = 80;
const REVENUE_LIMIT_NOTIFICATION_CLOSED_KEY = 'revenueLimitNotificationClosed';
enum RevenueLimitStageEnum {
  warning = 'warning',
  gracePeriod = 'gracePeriod',
  restricted = 'restricted',
}
const getRevenueLimitAccentClassName = (percentage: number) => {
  if (percentage >= 95) {
    return 'text-[#B91C1C] fill-[#B91C1C]';
  }
  if (percentage >= 90) {
    return 'text-[#FF4C35] fill-[#FF4C35]';
  }
  return 'text-[#FACC15] fill-[#FACC15]';
};
export const RevenueLimitNotification = () => {
  const $ = _c(52);
  const {
    t
  } = useLocoTranslation();
  const {
    user
  } = useUser();
  const {
    totalRevenueLimit
  } = useTotalRevenueLimit();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      useSessionStorage: true
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const notificationState = useSessionStore(REVENUE_LIMIT_NOTIFICATION_CLOSED_KEY, t0);
  let t1;
  if ($[1] !== notificationState) {
    t1 = () => notificationState.getItem() === true;
    $[1] = notificationState;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const [isClosed, setIsClosed] = useState(t1);
  let t2;
  if ($[3] !== totalRevenueLimit || $[4] !== user) {
    bb0: {
      if (!totalRevenueLimit || !user) {
        t2 = null;
        break bb0;
      }
      const revenueLimitInCents = totalRevenueLimit.maxLimit;
      const totalRevenueInCents = totalRevenueLimit.current;
      if (!revenueLimitInCents || totalRevenueInCents === null || totalRevenueInCents === undefined) {
        t2 = null;
        break bb0;
      }
      let t3;
      if ($[6] !== revenueLimitInCents || $[7] !== user.dashboardLocale) {
        t3 = currencyFormat(centsToUnits(revenueLimitInCents), CurrencyEnum.US_DOLLAR, user.dashboardLocale, true);
        $[6] = revenueLimitInCents;
        $[7] = user.dashboardLocale;
        $[8] = t3;
      } else {
        t3 = $[8];
      }
      const limit = t3;
      const percentage = Math.floor(totalRevenueInCents / revenueLimitInCents * 100);
      if (user.planLimitExceededAt && percentage >= MIN_REVENUE_LIMIT_PERCENTAGE) {
        const restrictionDate = user.restrictionEstimateDate ? new Date(user.restrictionEstimateDate) : null;
        if (restrictionDate && restrictionDate.getTime() > Date.now()) {
          t2 = {
            date: dateFormat(restrictionDate, {
              dateFormat: user.dateFormat,
              timeZone: user.timezone
            }),
            limit,
            percentage,
            stage: RevenueLimitStageEnum.gracePeriod as const
          };
          break bb0;
        }
        let t4;
        if ($[9] !== limit || $[10] !== percentage) {
          t4 = {
            limit,
            percentage,
            stage: RevenueLimitStageEnum.restricted as const
          };
          $[9] = limit;
          $[10] = percentage;
          $[11] = t4;
        } else {
          t4 = $[11];
        }
        t2 = t4;
        break bb0;
      }
      if (percentage < MIN_REVENUE_LIMIT_PERCENTAGE) {
        t2 = null;
        break bb0;
      }
      let t4;
      if ($[12] !== limit || $[13] !== percentage) {
        t4 = {
          limit,
          percentage,
          stage: RevenueLimitStageEnum.warning as const
        };
        $[12] = limit;
        $[13] = percentage;
        $[14] = t4;
      } else {
        t4 = $[14];
      }
      t2 = t4;
    }
    $[3] = totalRevenueLimit;
    $[4] = user;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const notificationData = t2;
  let t3;
  if ($[15] !== notificationState) {
    t3 = () => {
      notificationState.setItem(true);
      setIsClosed(true);
    };
    $[15] = notificationState;
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  const handleClose = t3;
  let t4;
  if ($[17] !== notificationData) {
    t4 = notificationData ?? {};
    $[17] = notificationData;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  const {
    limit: limit_0,
    stage
  } = t4;
  const isDismissible = stage === RevenueLimitStageEnum.warning;
  if (!notificationData || isClosed && isDismissible) {
    return null;
  }
  let accentClassName;
  let t5;
  let t6;
  let t7;
  if ($[19] !== limit_0 || $[20] !== notificationData.percentage || $[21] !== stage || $[22] !== t) {
    accentClassName = getRevenueLimitAccentClassName(notificationData.percentage);
    t7 = "border-fill-primary bg-background-secondary text-text-primary -mx-4 -mt-5 mb-5 border-y px-4 py-2.5 sm:-mx-6 sm:px-6 md:-mx-14 md:px-14";
    t6 = "flex items-center justify-between gap-2 text-sm";
    t5 = stage === RevenueLimitStageEnum.warning && <TransLoco t={t} i18nKey="dashboard.main.revenue_limit_notification" values={{
      maxLimit: limit_0
    }} components={{
      a: <LinkWithoutPrefetch className="primary-link" href={getPageRoute(pageRoute.ProfileUserPlan)} />,
      icon: <InfoIcon className={twJoin("mr-2 inline-block size-4", accentClassName)} />,
      percentage: <span className={accentClassName}>{notificationData.percentage}%</span>
    }} />;
    $[19] = limit_0;
    $[20] = notificationData.percentage;
    $[21] = stage;
    $[22] = t;
    $[23] = accentClassName;
    $[24] = t5;
    $[25] = t6;
    $[26] = t7;
  } else {
    accentClassName = $[23];
    t5 = $[24];
    t6 = $[25];
    t7 = $[26];
  }
  let t8;
  if ($[27] !== accentClassName || $[28] !== limit_0 || $[29] !== notificationData.date || $[30] !== stage || $[31] !== t) {
    t8 = stage === RevenueLimitStageEnum.gracePeriod && <TransLoco t={t} i18nKey="dashboard.main.revenue_limit_notification_grace_period" values={{
      date: notificationData.date,
      maxLimit: limit_0
    }} components={{
      a: <LinkWithoutPrefetch className="primary-link" href={getPageRoute(pageRoute.ProfileUserPlan)} />,
      icon: <InfoIcon className={twJoin("mr-2 inline-block size-4", accentClassName)} />,
      span: <span className={accentClassName} />
    }} />;
    $[27] = accentClassName;
    $[28] = limit_0;
    $[29] = notificationData.date;
    $[30] = stage;
    $[31] = t;
    $[32] = t8;
  } else {
    t8 = $[32];
  }
  let t9;
  if ($[33] !== accentClassName || $[34] !== limit_0 || $[35] !== stage || $[36] !== t) {
    t9 = stage === RevenueLimitStageEnum.restricted && <TransLoco t={t} i18nKey="dashboard.main.revenue_limit_notification_reached" values={{
      maxLimit: limit_0
    }} components={{
      a: <LinkWithoutPrefetch className="primary-link" href={getPageRoute(pageRoute.ProfileUserPlan)} />,
      icon: <InfoIcon className={twJoin("mr-2 inline-block size-4", accentClassName)} />
    }} />;
    $[33] = accentClassName;
    $[34] = limit_0;
    $[35] = stage;
    $[36] = t;
    $[37] = t9;
  } else {
    t9 = $[37];
  }
  let t10;
  if ($[38] !== t5 || $[39] !== t8 || $[40] !== t9) {
    t10 = <span>{t5}{t8}{t9}</span>;
    $[38] = t5;
    $[39] = t8;
    $[40] = t9;
    $[41] = t10;
  } else {
    t10 = $[41];
  }
  let t11;
  if ($[42] !== handleClose || $[43] !== isDismissible) {
    t11 = isDismissible && <button aria-label="Close revenue limit notification" className="group flex size-8 shrink-0 items-center justify-center outline-hidden" onClick={handleClose} type="button"><CloseIcon className="main-transition-colors group-hover:fill-text-primary group-focus-visible:fill-text-primary fill-fill-primary size-4" /></button>;
    $[42] = handleClose;
    $[43] = isDismissible;
    $[44] = t11;
  } else {
    t11 = $[44];
  }
  let t12;
  if ($[45] !== t10 || $[46] !== t11 || $[47] !== t6) {
    t12 = <div className={t6}>{t10}{t11}</div>;
    $[45] = t10;
    $[46] = t11;
    $[47] = t6;
    $[48] = t12;
  } else {
    t12 = $[48];
  }
  let t13;
  if ($[49] !== t12 || $[50] !== t7) {
    t13 = <div className={t7}>{t12}</div>;
    $[49] = t12;
    $[50] = t7;
    $[51] = t13;
  } else {
    t13 = $[51];
  }
  return t13;
};